import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Link as MuiLink,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { COVERAGE_MODES, SERVICE_PROGRAMS, TRIBAL_OPTIONS, US_STATES } from '@app/shared';
import type {
  CoverageMode,
  ServiceAreaCoverageRow,
  ServiceAreaDto,
  ServiceAreaSummaryRow,
  ServiceProgram,
  TribalOption,
} from '@app/shared';
import { DataTable, rowNumber } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { StatusChip } from '../../components/StatusChip';
import { ConfirmDialog } from '../../components/dialogs';
import { ApiError } from '../../lib/api';
import { useFeatureReleased } from '../../release';
import {
  useAreaSummary,
  useAreaZips,
  useBulkArea,
  useCoverage,
  useImportCoverage,
  useRemoveArea,
  useUpsertArea,
} from './api';

const ROUTING = 'zip-carrier-routing';

type Programs = Record<ServiceProgram, boolean>;
type Problems = Partial<Record<'zips' | 'programs' | 'file', string>>;

const NO_PROGRAMS: Programs = { lifeline: false, nonLifeline: false, ebb: false };
const MAX_ZIPS = 5000;
const STATE_NAME = new Map<string, string>(US_STATES.map(([code, name]) => [code, name]));

/** Keeps the native file input in the tab order while only the button is visible. */
const VISUALLY_HIDDEN = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
} as const;

/** ZIP codes typed one per line or separated by commas or spaces; repeats are dropped. */
const parseZips = (text: string): string[] => [
  ...new Set(
    text
      .split(/[\s,;]+/)
      .map((z) => z.trim())
      .filter(Boolean),
  ),
];

function checkZips(text: string): { zips: string[]; problem?: string } {
  const zips = parseZips(text);
  if (zips.length === 0) return { zips, problem: 'Enter at least one ZIP code' };
  if (zips.length > MAX_ZIPS) return { zips, problem: `At most ${MAX_ZIPS} ZIP codes at a time` };
  const bad = zips.filter((z) => !/^\d{5}$/.test(z));
  if (bad.length) return { zips, problem: `Not a 5-digit ZIP code: ${bad.slice(0, 5).join(', ')}` };
  return { zips };
}

/** Field errors of the API (`zips.3.zip`, `programs`) mapped to the two inputs of these forms. */
function serverProblems(err: unknown): Problems | null {
  if (!(err instanceof ApiError) || err.details.length === 0) return null;
  const found: Problems = {};
  for (const d of err.details) {
    const key = d.path.split('.')[0];
    if (key === 'zips' || key === 'programs') found[key] ??= d.message;
  }
  return Object.keys(found).length ? found : null;
}

function ProgramBoxes(props: {
  value: Programs;
  onChange: (next: Programs) => void;
  error?: string;
}) {
  return (
    <FormControl error={Boolean(props.error)} component="fieldset">
      <FormLabel component="legend" sx={{ fontSize: 13 }}>
        Program *
      </FormLabel>
      <FormGroup row>
        {SERVICE_PROGRAMS.map(([key, label]) => (
          <FormControlLabel
            key={key}
            label={label}
            control={
              <Checkbox
                size="small"
                checked={props.value[key]}
                onChange={(e) => props.onChange({ ...props.value, [key]: e.target.checked })}
              />
            }
          />
        ))}
      </FormGroup>
      {props.error ? <FormHelperText sx={{ mx: 0 }}>{props.error}</FormHelperText> : null}
    </FormControl>
  );
}

function ZipBox(props: { value: string; onChange: (text: string) => void; error?: string }) {
  return (
    <TextField
      multiline
      minRows={4}
      maxRows={10}
      required
      label="Zip Code"
      placeholder="One per line, or separated by commas"
      value={props.value}
      onChange={(e) => props.onChange(e.target.value)}
      error={Boolean(props.error)}
      helperText={props.error ?? `One per line or comma separated, at most ${MAX_ZIPS}.`}
      sx={{ gridColumn: '1 / -1' }}
    />
  );
}

/** "Add/Update Area": opens ZIP codes for the ticked programmes. */
function AddUpdateForm() {
  const notify = useNotify();
  const upsert = useUpsertArea();
  const [active, setActive] = useState(true);
  const [tribal, setTribal] = useState<TribalOption>('No');
  const [text, setText] = useState('');
  const [programs, setPrograms] = useState<Programs>(NO_PROGRAMS);
  const [problems, setProblems] = useState<Problems>({});

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const { zips, problem } = checkZips(text);
    const found: Problems = { zips: problem };
    if (!Object.values(programs).some(Boolean)) found.programs = 'Tick at least one programme';
    setProblems(found);
    if (found.zips || found.programs) return;
    try {
      const result = await upsert.mutateAsync({
        zips: zips.map((zip) => ({ zip })),
        active,
        tribal,
        programs,
      });
      notify.success(`${result.added} ZIP code(s) added, ${result.updated} updated.`);
      setText('');
    } catch (err) {
      const fromServer = serverProblems(err);
      if (fromServer) setProblems(fromServer);
      else notify.error(err);
    }
  };

  return (
    <Box
      component="form"
      noValidate
      onSubmit={(e) => void submit(e)}
      aria-label="Add/Update Area"
      sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' } }}
    >
      <TextField
        select
        label="Status"
        value={active ? 'Yes' : 'No'}
        onChange={(e) => setActive(e.target.value === 'Yes')}
      >
        <MenuItem value="Yes">Yes</MenuItem>
        <MenuItem value="No">No</MenuItem>
      </TextField>
      <TextField
        select
        label="Is Tribal"
        value={tribal}
        onChange={(e) => setTribal(e.target.value as TribalOption)}
      >
        {TRIBAL_OPTIONS.map((t) => (
          <MenuItem key={t} value={t}>
            {t}
          </MenuItem>
        ))}
      </TextField>
      <ZipBox
        value={text}
        onChange={(v) => {
          setText(v);
          setProblems((p) => ({ ...p, zips: undefined }));
        }}
        error={problems.zips}
      />
      <Box sx={{ gridColumn: '1 / -1' }}>
        <ProgramBoxes
          value={programs}
          onChange={(next) => {
            setPrograms(next);
            setProblems((p) => ({ ...p, programs: undefined }));
          }}
          error={problems.programs}
        />
      </Box>
      <Box>
        <Button type="submit" variant="contained" disabled={upsert.isPending}>
          {upsert.isPending ? 'Saving…' : 'Submit'}
        </Button>
      </Box>
    </Box>
  );
}

/** "Delete Area": closes ZIP codes for the ticked programmes. */
function DeleteForm() {
  const notify = useNotify();
  const remove = useRemoveArea();
  const [text, setText] = useState('');
  const [programs, setPrograms] = useState<Programs>(NO_PROGRAMS);
  const [problems, setProblems] = useState<Problems>({});
  const [pending, setPending] = useState<string[] | null>(null);

  const ask = (e: FormEvent) => {
    e.preventDefault();
    const { zips, problem } = checkZips(text);
    const found: Problems = { zips: problem };
    if (!Object.values(programs).some(Boolean)) found.programs = 'Tick at least one programme';
    setProblems(found);
    if (!found.zips && !found.programs) setPending(zips);
  };

  const confirm = async () => {
    if (!pending) return;
    try {
      const result = await remove.mutateAsync({ zips: pending, programs });
      notify.success(
        `${result.changed} ZIP code(s) changed, ${result.removed} removed from the service area.`,
      );
      setText('');
    } catch (err) {
      const fromServer = serverProblems(err);
      if (fromServer) setProblems(fromServer);
      else notify.error(err);
    } finally {
      setPending(null);
    }
  };

  const ticked = SERVICE_PROGRAMS.filter(([key]) => programs[key]).map(([, label]) => label);

  return (
    <Box
      component="form"
      noValidate
      onSubmit={ask}
      aria-label="Delete Area"
      sx={{ display: 'grid', gap: 2 }}
    >
      <ZipBox
        value={text}
        onChange={(v) => {
          setText(v);
          setProblems((p) => ({ ...p, zips: undefined }));
        }}
        error={problems.zips}
      />
      <ProgramBoxes
        value={programs}
        onChange={(next) => {
          setPrograms(next);
          setProblems((p) => ({ ...p, programs: undefined }));
        }}
        error={problems.programs}
      />
      <Box>
        <Button type="submit" variant="contained" color="error" disabled={remove.isPending}>
          Delete
        </Button>
      </Box>
      <ConfirmDialog
        open={Boolean(pending)}
        title="Delete Area"
        danger
        busy={remove.isPending}
        confirmLabel="Delete"
        message={`Close ${pending?.length ?? 0} ZIP code(s) for ${ticked.join(', ')}? A ZIP code left with no programme is removed from the list.`}
        onConfirm={() => void confirm()}
        onClose={() => setPending(null)}
      />
    </Box>
  );
}

/** "Bulk Add Service Zip Codes": a CSV file with a `zip` column. */
function BulkForm() {
  const notify = useNotify();
  const bulk = useBulkArea();
  const [file, setFile] = useState<File | null>(null);
  // Remounts the file input after a submit; a file input cannot be cleared through its value.
  const [fileKey, setFileKey] = useState(0);
  const [problem, setProblem] = useState<string>();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!file) {
      setProblem('Choose a CSV file');
      return;
    }
    try {
      const result = await bulk.mutateAsync(file);
      notify.success(`${result.added} ZIP code(s) added, ${result.updated} updated.`);
      setFile(null);
      setFileKey((k) => k + 1);
    } catch (err) {
      // A bad row rejects the whole file; show which one next to the file.
      setProblem(
        err instanceof ApiError && err.details[0]
          ? `${err.details[0].message} (${err.details[0].path})`
          : err instanceof Error
            ? err.message
            : 'The file could not be processed.',
      );
    }
  };

  return (
    <Box
      component="form"
      noValidate
      onSubmit={(e) => void submit(e)}
      aria-label="Bulk Add Service Zip Codes"
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Button
          component="label"
          role={undefined}
          tabIndex={-1}
          variant="outlined"
          startIcon={<UploadFileIcon />}
        >
          Choose CSV file
          <Box
            component="input"
            key={fileKey}
            type="file"
            accept=".csv,text/csv"
            aria-label="CSV file"
            sx={VISUALLY_HIDDEN}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setFile(e.target.files?.[0] ?? null);
              setProblem(undefined);
            }}
          />
        </Button>
        <Typography>{file ? file.name : 'No file chosen'}</Typography>
      </Box>
      <FormHelperText error={Boolean(problem)}>
        {problem ??
          'Columns: zip (required); city, state, active, tribal, lifeline, nonlifeline, ebb (optional, yes/no). Without programme columns the ZIP codes are opened for Non-Lifeline. At most 5000 rows and 2 MB.'}
      </FormHelperText>
      <Box sx={{ mt: 2 }}>
        <Button type="submit" variant="contained" disabled={bulk.isPending}>
          {bulk.isPending ? 'Processing…' : 'Submit'}
        </Button>
      </Box>
    </Box>
  );
}

/**
 * ZIP codes of one state (or, with ZIP carrier routing, of one carrier's coverage list). The
 * reference's Area List shows counts only; what its rows open was not observed, so this list is
 * this project's own way to see what is configured.
 */
function ZipsDialog({
  filter,
  title,
  onClose,
}: {
  filter: { state: string } | { carrierId: string } | null;
  title: string;
  onClose: () => void;
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const zips = useAreaZips({ ...(filter ?? {}), page, pageSize }, Boolean(filter));
  const routing = useFeatureReleased()(ROUTING);

  const columns: Column<ServiceAreaDto>[] = [
    { key: '#', header: '#', width: 50, render: (_z, i) => rowNumber(i, page, pageSize) },
    { key: 'zip', header: 'Zip Code', render: (z) => z.zip },
    { key: 'city', header: 'City', render: (z) => z.city },
    {
      key: 'active',
      header: 'Status',
      render: (z) => <StatusChip status={z.active ? 'Active' : 'Inactive'} />,
    },
    { key: 'tribal', header: 'Tribal', render: (z) => z.tribal },
    {
      key: 'programs',
      header: 'Program',
      render: (z) =>
        SERVICE_PROGRAMS.filter(([key]) => z.programs[key])
          .map(([, label]) => label)
          .join(', '),
    },
    ...(routing
      ? [
          {
            key: 'carriers',
            header: 'Carriers',
            render: (z: ServiceAreaDto) =>
              z.carriers.map((c) => c.code).join(', ') || 'Every carrier',
          },
        ]
      : []),
  ];

  return (
    <Dialog
      open={Boolean(filter)}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionProps={{ onExited: () => setPage(1) }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <DataTable
          label="Zip codes"
          columns={columns}
          rows={zips.data?.data}
          rowKey={(z) => z.id}
          loading={zips.isFetching}
          error={zips.error}
          onRetry={() => void zips.refetch()}
          page={page}
          pageSize={pageSize}
          total={zips.data?.total ?? 0}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          pageSizeOptions={[10, 20, 50, 100]}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * ZIP carrier routing (owner, 2026-11-05): a carrier's coverage file. The carrier then serves
 * the listed ZIPs; a ZIP whose list names no carrier is open to every carrier.
 */
function CoverageForm({ carriers }: { carriers: ServiceAreaCoverageRow[] }) {
  const notify = useNotify();
  const upload = useImportCoverage();
  const [carrierId, setCarrierId] = useState('');
  const [mode, setMode] = useState<CoverageMode>('Add');
  const [file, setFile] = useState<File | null>(null);
  const [fileKey, setFileKey] = useState(0);
  const [problems, setProblems] = useState<Partial<Record<'carrierId' | 'file', string>>>({});

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const missing = {
      ...(carrierId ? {} : { carrierId: 'Select Carrier' }),
      ...(file ? {} : { file: 'Choose a CSV file' }),
    };
    setProblems(missing);
    if (!carrierId || !file) return;
    try {
      const r = await upload.mutateAsync({ carrierId, mode, file });
      const code = carriers.find((c) => c.carrierId === carrierId)?.code ?? '';
      notify.success(
        `${code}: ${r.matched} ZIP code(s) ${mode === 'Remove' ? 'taken off' : 'covered'}` +
          (r.removed ? `, taken off ${r.removed} other(s)` : '') +
          (r.notInArea ? `; ${r.notInArea} not in the service area, left out` : '') +
          '.',
      );
      setFile(null);
      setFileKey((k) => k + 1);
    } catch (err) {
      setProblems({
        file:
          err instanceof ApiError && err.details[0]
            ? err.details[0].message
            : err instanceof Error
              ? err.message
              : 'The file could not be processed.',
      });
    }
  };

  return (
    <Box component="form" noValidate onSubmit={(e) => void submit(e)} aria-label="Carrier Coverage">
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <TextField
          select
          label="Carrier"
          required
          value={carrierId}
          onChange={(e) => setCarrierId(e.target.value)}
          error={Boolean(problems.carrierId)}
          helperText={problems.carrierId}
          sx={{ width: { xs: '100%', sm: 240 } }}
        >
          {carriers.map((c) => (
            <MenuItem key={c.carrierId} value={c.carrierId}>
              {c.code} ({c.name})
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="What to do"
          value={mode}
          onChange={(e) => setMode(e.target.value as CoverageMode)}
          sx={{ width: { xs: '100%', sm: 300 } }}
          helperText={
            mode === 'Add'
              ? 'The carrier also serves the ZIP codes of the file.'
              : mode === 'Replace'
                ? 'The carrier serves exactly the ZIP codes of the file.'
                : 'The carrier no longer serves the ZIP codes of the file.'
          }
        >
          {COVERAGE_MODES.map((m) => (
            <MenuItem key={m} value={m}>
              {m}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mt: 1 }}>
        <Button
          component="label"
          role={undefined}
          tabIndex={-1}
          variant="outlined"
          startIcon={<UploadFileIcon />}
        >
          Choose CSV file
          <Box
            component="input"
            key={fileKey}
            type="file"
            accept=".csv,text/csv"
            aria-label="Coverage file"
            sx={VISUALLY_HIDDEN}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setFile(e.target.files?.[0] ?? null);
              setProblems((p) => ({ ...p, file: undefined }));
            }}
          />
        </Button>
        <Typography>{file ? file.name : 'No file chosen'}</Typography>
      </Box>
      <FormHelperText error={Boolean(problems.file)}>
        {problems.file ??
          "The carrier's or aggregator's coverage file: a zip column, one ZIP code per row. Only ZIP codes already in the service area change; at most 50000 rows and 2 MB."}
      </FormHelperText>
      <Box sx={{ mt: 2 }}>
        <Button type="submit" variant="contained" disabled={upload.isPending}>
          {upload.isPending ? 'Processing…' : 'Submit'}
        </Button>
      </Box>
    </Box>
  );
}

type FormTab = 'add' | 'delete' | 'bulk' | 'coverage';

/**
 * Service Area (PC237): where the company sells, ZIP code by ZIP code and programme by
 * programme. The reference's "Company" select is left out: this build has one company.
 */
export function ServiceAreaPage() {
  const summary = useAreaSummary();
  const [tab, setTab] = useState<FormTab>('add');
  const [state, setState] = useState<string | null>(null);
  const routing = useFeatureReleased()(ROUTING);
  const coverage = useCoverage(routing);
  const [carrier, setCarrier] = useState<ServiceAreaCoverageRow | null>(null);
  const carriers = (coverage.data ?? []).filter((c) => c.carrierId);

  const coverageColumns: Column<ServiceAreaCoverageRow>[] = [
    {
      key: 'carrier',
      header: 'Carrier',
      render: (r) =>
        r.carrierId && r.zips > 0 ? (
          <MuiLink
            component="button"
            type="button"
            onClick={() => setCarrier(r)}
            aria-label={`View zip codes of ${r.code}`}
          >
            {r.code} ({r.name})
          </MuiLink>
        ) : r.carrierId ? (
          `${r.code} (${r.name})`
        ) : (
          r.name
        ),
    },
    { key: 'zips', header: 'Zip Codes', align: 'right', render: (r) => r.zips },
  ];

  const columns: Column<ServiceAreaSummaryRow>[] = [
    { key: '#', header: '#', width: 50, render: (_r, i) => i + 1 },
    {
      key: 'state',
      header: 'State',
      render: (r) => {
        const name = `${STATE_NAME.get(r.state) ?? r.state} (${r.state})`;
        return r.activeZips + r.inactiveZips > 0 ? (
          <MuiLink
            component="button"
            type="button"
            onClick={() => setState(r.state)}
            aria-label={`View zip codes of ${r.state}`}
          >
            {name}
          </MuiLink>
        ) : (
          name
        );
      },
    },
    { key: 'activeZips', header: 'Active Zip', align: 'right', render: (r) => r.activeZips },
    { key: 'inactiveZips', header: 'Inactive Zip', align: 'right', render: (r) => r.inactiveZips },
  ];

  return (
    <Box>
      <PageHeader title="Service Area" code="PC237" />
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_e, value: FormTab) => setTab(value)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          aria-label="Service area actions"
        >
          <Tab value="add" label="Add/Update Area" />
          <Tab value="delete" label="Delete Area" />
          <Tab value="bulk" label="Bulk Add Service Zip Codes" />
          {routing ? <Tab value="coverage" label="Carrier Coverage" /> : null}
        </Tabs>
        {tab === 'add' ? (
          <AddUpdateForm />
        ) : tab === 'delete' ? (
          <DeleteForm />
        ) : tab === 'coverage' && routing ? (
          <CoverageForm carriers={carriers} />
        ) : (
          <BulkForm />
        )}
      </Paper>

      <Typography variant="h3" sx={{ mb: 1 }}>
        Area List
      </Typography>
      <DataTable
        label="Area List"
        columns={columns}
        rows={summary.data}
        rowKey={(r) => r.state}
        loading={summary.isFetching}
        error={summary.error}
        onRetry={() => void summary.refetch()}
      />
      {routing ? (
        <>
          <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
            Carrier Coverage
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            An enrollment offers the carriers that cover its ZIP code, in the order of System Config
            SC73 (Priority Wise Carrier List For CSA In Enrollment); with SC72 = Y each one is also
            asked through its connection.
          </Typography>
          <DataTable
            label="Carrier Coverage"
            columns={coverageColumns}
            rows={coverage.data}
            rowKey={(r) => r.carrierId || 'every'}
            loading={coverage.isFetching}
            error={coverage.error}
            onRetry={() => void coverage.refetch()}
          />
        </>
      ) : null}
      <ZipsDialog
        filter={state ? { state } : carrier ? { carrierId: carrier.carrierId } : null}
        title={
          state
            ? `Zip codes in ${STATE_NAME.get(state) ?? state} (${state})`
            : carrier
              ? `Zip codes covered by ${carrier.code}`
              : ''
        }
        onClose={() => {
          setState(null);
          setCarrier(null);
        }}
      />
    </Box>
  );
}
